import React from 'react';

import { Box, Divider, Grid, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';

import {
  ActiveInactive,
  ConsumerDocument,
  ConsumerQuery,
  useConsumerUpdateMutation,
} from 'src/api';
import { Button } from 'src/components/common';
import { Field, Form, RadioGroupField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useScreenInfo } from 'src/hooks/common';

import { VerificationPreferenceSettingsValues } from './constants';
import { containerStyles } from './styles';
import {
  PrivacySettingsRequirementOptions,
  VerificationPreferenceFormFields,
  VerificationPreferenceFormValues,
} from './types';
import { getVerificationPreferenceInitialValues } from './utils';

interface VerificationPreferenceFormProps {
  consumer: Nullable<ConsumerQuery['employee']>;
}

export const VerificationPreferenceForm: React.FC<VerificationPreferenceFormProps> = ({
  consumer,
}) => {
  const { isMobile } = useScreenInfo();
  const { createApolloContext } = useNotification();

  const [consumerUpdate] = useConsumerUpdateMutation({
    refetchQueries: [ConsumerDocument],
    context: createApolloContext({
      successMessage: 'The changes have been saved',
    }),
  });

  const onSubmit = React.useCallback(
    async (data: VerificationPreferenceFormValues) => {
      try {
        await consumerUpdate({
          variables: {
            where: { id: consumer?.id },
            data: {
              requestAutoApprove:
                data.verificationPreference === PrivacySettingsRequirementOptions.allowAll
                  ? ActiveInactive.Active
                  : ActiveInactive.Inactive,
            },
          },
        });
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
        }
      }
    },
    [consumer?.id, consumerUpdate],
  );

  const isDisabledForm = React.useMemo(
    () => consumer?.freeze === ActiveInactive.Active,
    [consumer?.freeze],
  );

  const initialValues: VerificationPreferenceFormValues = React.useMemo(() => {
    return getVerificationPreferenceInitialValues(consumer?.requestAutoApprove);
  }, [consumer?.requestAutoApprove]);

  return (
    <Box>
      <Form onSubmit={onSubmit} initialValues={initialValues}>
        {({ form: { reset }, submitting }) => (
          <Grid container>
            <Grid sx={containerStyles} mb={3} size={12}>
              <Box mb={3}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
                  Verification Preference
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Please select below whether or not you want to have the ability to approve/decline
                  verification requests made to you
                </Typography>
              </Box>

              <Divider />

              <Grid mt={2} size={12}>
                <Field
                  name={VerificationPreferenceFormFields.verificationPreference}
                  component={RadioGroupField}
                  options={VerificationPreferenceSettingsValues}
                  disabled={submitting || isDisabledForm}
                />
              </Grid>
            </Grid>

            <Grid
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={isMobile ? 2 : 'auto'}
              size={12}
            >
              <Button
                type="reset"
                variant="outlined"
                color="error"
                onClick={() => reset()}
                disabled={submitting || isDisabledForm}
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                Reset
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={submitting || isDisabledForm}
                loading={submitting}
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        )}
      </Form>
    </Box>
  );
};
